/**
 * Raw design token values for SignalSentry client app.
 *
 * Sandpiper of California (SOC) tactical palette, two modes (dark + light).
 * The 70-20-10 application rule applies:
 *   70% foundation (Stealth Black or Chalk White, plus surfaces)
 *   20% secondary neutrals (Coyote Brown borders, Shadow Gray text)
 *   10% accent (Tactical Gold for state, focus, primary CTAs)
 *
 * Locked SOC palette (anchor hex values, regardless of mode):
 *   Tactical Gold:  #FFB400  (primary accent, dark mode + dark on light)
 *   Tactical Gold (light-mode): #D4AF37 (deeper for AAA contrast on Chalk White)
 *   Stealth Black:  #000000  (dark-mode foundation)
 *   Coyote Brown:   #D99E44  (secondary neutral / borders / icons)
 *   Shadow Gray:    #364355  (light-mode secondary text)
 *   Chalk White:    #F7F5ED  (light-mode foundation, dark-mode primary text)
 *
 * Two exports:
 *   - darkTokenValues: bound to [data-theme="dark"] selector at build time
 *   - lightTokenValues: bound to [data-theme="light"] selector at build time
 *
 * The legacy `tokenValues` export is preserved as an alias for `darkTokenValues`
 * so consumers that read raw values directly (e.g. inline-style colors in the
 * NOW page concentric viz) keep working without touching every call site.
 *
 * IMPORTANT: Do not edit these values without updating the design spec.
 * All motion, color, and typographic decisions are locked per Phase 4 amendment.
 */

/**
 * Dark-mode token tree (default). Foundation is Stealth Black with near-black
 * surfaces; accent is full-saturation Tactical Gold; body text is Chalk White.
 */
export const darkTokenValues = {
  colors: {
    bg: {
      // Deep matte charcoal with subtle cool undertone, inspired by premium
      // tactical product finishes (matte camera body, ZSA Moonlander).
      // Pure #000000 reads inky and cheap on large surfaces; #0e0f12 has the
      // depth a textured matte black needs while staying near-black.
      base: '#0e0f12',
      surface1: '#15171c',
      surface2: '#1c1f25',
      surface3: '#252830',
    },
    border: {
      subtle: '#2a2d34',
      default: '#3a3e46',
      strong: '#4a4f59',
      focus: '#FFB400',
    },
    text: {
      primary: '#F7F5ED',
      secondary: '#a8a39a',
      tertiary: '#6b665d',
    },
    accent: {
      gold: '#FFB400',
      goldDim: '#8a6200',
      goldGlow: 'rgba(255, 180, 0, 0.18)',
    },
    status: {
      ok: '#4ade80',
      warn: '#FFB400',
      alert: '#f87171',
      critical: '#ef4444',
    },
  },
  fonts: {
    sans: '"Geist", system-ui, sans-serif',
    mono: '"Geist Mono", ui-monospace, monospace',
  },
  sizes: {
    base: '13.5px',
    hero: '96px',
    heroSmall: '72px',
    label: '11px',
  },
  radii: {
    sm: '4px',
    md: '6px',
    lg: '10px',
    xl: '14px',
  },
  motion: {
    duration: {
      instant: '60ms',
      fast: '140ms',
      base: '200ms',
      slow: '320ms',
    },
    ease: {
      out: 'cubic-bezier(0.16, 1, 0.3, 1)',
      in: 'cubic-bezier(0.7, 0, 0.84, 0)',
      inOut: 'cubic-bezier(0.65, 0, 0.35, 1)',
    },
  },
} as const;

/**
 * Light-mode token tree. Foundation is Chalk White with off-white surfaces.
 * Accent is the deeper Tactical Gold (#D4AF37) for AAA contrast against the
 * near-white background; body text is Stealth Black with Shadow Gray secondary.
 *
 * Borders follow the Coyote Brown lineage, lightened progressively from
 * subtle (parchment) to strong (full Coyote Brown).
 *
 * Status colors shift toward higher-saturation web-safe values that pop on
 * a light background (compare #4ade80 lime vs #16a34a forest green).
 *
 * Fonts / sizes / radii / motion are mode-invariant.
 */
export const lightTokenValues = {
  colors: {
    bg: {
      // Warm matte greige (NOT flat off-white). #E8E5DD aligns with the SOC
      // tactical/desert aesthetic (sits in the same family as Coyote Brown)
      // and reads premium-matte the way Tesla cream interior or Arc light
      // mode does. Surfaces lift from this base to bright Chalk White at the
      // top of the stack so card content still pops.
      base: '#E8E5DD',
      surface1: '#F0EDE4',
      surface2: '#F7F5ED',
      surface3: '#FBFAF4',
    },
    border: {
      subtle: '#d6d1c2',
      default: '#bcb5a0',
      strong: '#9b8f6e',
      focus: '#FFB400',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#364355',
      tertiary: '#6b7280',
    },
    accent: {
      gold: '#D4AF37',
      goldDim: '#8a6200',
      goldGlow: 'rgba(255, 180, 0, 0.15)',
    },
    status: {
      ok: '#16a34a',
      warn: '#D4AF37',
      alert: '#dc2626',
      critical: '#b91c1c',
    },
  },
  fonts: darkTokenValues.fonts,
  sizes: darkTokenValues.sizes,
  radii: darkTokenValues.radii,
  motion: darkTokenValues.motion,
} as const;

/**
 * Backwards-compatible alias for the dark token tree. Existing call sites that
 * imported `tokenValues` (concentric-geometry, AlertRing, ConcentricZonesViz)
 * keep using the dark-mode hex values for inline-style fills. Components that
 * route through vanilla-extract's `theme` contract (95% of UI surfaces) pick
 * up the live theme via the data-attribute selector binding in tokens.css.ts.
 */
export const tokenValues = darkTokenValues;

/** Type representing the full token value tree. */
export type TokenValues = typeof darkTokenValues;
